import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { useAuthMe } from '@/features/auth/hooks/useAuthMe';
import { useAuthStore } from '@/store/authStore';
import { userPreferencesStorage } from '@/lib/storage/userPreferences';
import type { LanguageCode } from '@/types/userPreferences';

export function useLanguagePreference() {
  const { i18n } = useTranslation();
  const { data: user, isLoading } = useAuthMe();
  const accessToken = useAuthStore(state => state.accessToken);

  // Sync user's language preference on login/logout
  useEffect(() => {
    if (isLoading) return;

    if (accessToken && user?.id) {
      // User is logged in - load their language preference
      const preferences = userPreferencesStorage.get(user.id);
      const preferredLanguage = preferences.language || 'vi';

      if (i18n.language !== preferredLanguage) {
        void i18n.changeLanguage(preferredLanguage);
      }
    } else {
      // User is logged out - reset to default Vietnamese
      // Check if language is already default to avoid unnecessary re-renders
      if (i18n.language !== 'vi') {
        void i18n.changeLanguage('vi');
      }
    }
  }, [accessToken, user?.id, isLoading, i18n]);

  const updateLanguage = (language: LanguageCode) => {
    if (!accessToken || !user?.id) {
      // If not logged in, update UI but don't persist
      void i18n.changeLanguage(language);
      return;
    }

    // Update localStorage for this user
    userPreferencesStorage.setLanguage(user.id, language);

    // Update UI immediately
    void i18n.changeLanguage(language);
  };

  return {
    currentLanguage: i18n.language as LanguageCode,
    updateLanguage,
    isLoading,
  };
}
